import type { Config } from '@puckeditor/core';

import { buttonConfig } from './button';
import { flexLayoutConfig } from './flex-layout';
import { headingConfig } from './heading';
import { heroConfig } from './hero';
import { imageConfig } from './image';
import { paragraphConfig } from './paragraph';
import { productCarouselConfig } from './product-carousel/product-carousel';
import { sectionConfig } from './section';
import { slideshowConfig } from './slideshow';
import { spacerConfig } from './spacer';
import { splitBannerConfig } from './split-banner';

type Components = NonNullable<Config['components']>;

/**
 * Generic page-building blocks, usable by any CMS model. A model opts in by
 * spreading this whole map into its Puck config, or by picking the entries it
 * wants — nothing here assumes a particular model.
 *
 * **Adding a block here reaches all four models, and that is a deliberate invariant rather than a
 * side effect.** `header` and `footer` spread this bundle wholesale, and their own tests assert
 * `registers exactly the same component set as standalone-page` under an explicit "Product
 * requirement" comment, deriving the expectation from standalone-page's config so it fails when a
 * block is added there but not mirrored. So a block cannot be given to standalone-page alone
 * without either mirroring it or retiring that requirement — which is a product decision, not a
 * side effect of adding a component.
 */
export const PAGE_BLOCKS: Components = {
  Hero: heroConfig,
  Section: sectionConfig,
  FlexLayout: flexLayoutConfig,
  Spacer: spacerConfig,
  Heading: headingConfig,
  Paragraph: paragraphConfig,
  Image: imageConfig,
  Button: buttonConfig,
  Slideshow: slideshowConfig,
  SplitBanner: splitBannerConfig,
};

/**
 * Blocks that render the merchant's catalog. Kept out of PAGE_BLOCKS because they need
 * a resolved dispensary, whereas everything in PAGE_BLOCKS renders from its own props
 * alone — a model should be able to take the generic blocks without the commerce ones.
 */
export const COMMERCE_BLOCKS: Components = {
  ProductCarousel: productCarouselConfig,
};

/** Commerce-category block names, in the order they should appear in the panel. */
export const COMMERCE_BLOCK_NAMES = ['ProductCarousel'];

/** Layout-category block names, in the order they should appear in the panel. */
export const PAGE_LAYOUT_BLOCK_NAMES = ['Hero', 'Section', 'FlexLayout', 'SplitBanner', 'Spacer'];

/** Content-category block names, in the order they should appear in the panel. */
export const PAGE_CONTENT_BLOCK_NAMES = ['Heading', 'Paragraph', 'Image', 'Button', 'Slideshow'];
